import { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QueueListIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  AI_TAG_TRACES_LIMIT_DEFAULT,
  type AiTagTrace,
  type AiTagTracesResult,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { fetchAiTraces } from './ai-trace-api';

/**
 * « JOURNAL DES APPELS IA » — chaque appel du tagging IA au modèle de vision, avec le
 * PROMPT envoyé (taxonomie + interdiction de créer des tags) et la RÉPONSE brute du modèle.
 *
 * SECTION de la console d'ADMINISTRATION (réservée au superadmin, comme la Configuration
 * LLM) : rendue (`role="tabpanel"`, `<h3>`) dans le corps défilant fourni par `AdminPanel`
 * — pas d'en-tête/close/scroll propres. Lecture seule.
 */

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

export function AiTracePage({ libraryId }: { libraryId: string }): React.JSX.Element {
  const { t, locale } = useI18n();

  const [page, setPage] = useState(0);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [data, setData] = useState<AiTagTracesResult | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const limit = AI_TAG_TRACES_LIMIT_DEFAULT;
  const offset = page * limit;

  useEffect(() => {
    if (!libraryId) {
      setStatus('idle');
      setData(null);
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchAiTraces(libraryId, limit, offset, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return;
        setData(res);
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [libraryId, limit, offset, refreshKey]);

  const traces = data?.traces ?? [];
  const total = data?.total ?? 0;

  return (
    <section
      id="admin-aitrace"
      role="tabpanel"
      aria-labelledby="admin-aitrace-heading"
      className="flex flex-col gap-6"
    >
      {/* En-tête de section (l'en-tête de page + le fil d'Ariane viennent d'AdminPanel). */}
      <div className="flex flex-col gap-1">
        <h3
          id="admin-aitrace-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <QueueListIcon className="h-5 w-5" aria-hidden="true" />
          {t('aiTrace.title')}
        </h3>
        <p className="max-w-2xl text-sm text-muted">{t('aiTrace.intro')}</p>
      </div>

      <div className="flex flex-col gap-6">
        {status === 'error' ? (
          <div className="flex flex-col items-center gap-3 py-12 text-center" role="alert">
            <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
            <p className="text-fg">{t('aiTrace.errorLoad')}</p>
            <button
              type="button"
              onClick={() => setRefreshKey((k) => k + 1)}
              className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('aiTrace.retry')}
            </button>
          </div>
        ) : status === 'loading' || status === 'idle' ? (
          <p className="py-12 text-center text-muted" role="status">
            {t('aiTrace.loading')}
          </p>
        ) : traces.length > 0 ? (
          <>
            <p className="text-sm font-medium text-fg">
              {interpolate(t('aiTrace.total'), { count: total }, locale)}
            </p>
            <ul className="flex flex-col gap-4">
              {traces.map((trace) => (
                <TraceCard key={trace.id} trace={trace} />
              ))}
            </ul>
            <Pagination
              page={page}
              limit={limit}
              total={total}
              loaded={traces.length}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => p + 1)}
            />
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 py-16 text-center text-muted">
            <QueueListIcon className="h-10 w-10" aria-hidden="true" />
            <p className="font-medium text-fg">{t('aiTrace.empty')}</p>
            <p className="max-w-md text-sm">{t('aiTrace.emptyHint')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

/** Une trace : en-tête (modèle, joignabilité, ventilation) + prompt envoyé + réponse brute. */
function TraceCard({ trace }: { trace: AiTagTrace }): React.JSX.Element {
  const { t, locale } = useI18n();
  const when = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'medium' }).format(
    new Date(trace.createdAt),
  );
  return (
    <li className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="text-muted">{when}</span>
        <span className="font-medium text-fg">
          {t('aiTrace.model')} : {trace.model}
        </span>
        {trace.reachable ? (
          <span className="inline-flex items-center gap-1 text-brand">
            <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
            {t('aiTrace.reachable')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-danger">
            <XCircleIcon className="h-4 w-4" aria-hidden="true" />
            {t('aiTrace.unreachable')}
          </span>
        )}
        <span className="text-muted">
          {interpolate(t('aiTrace.summary'), {
            linked: trace.linkedCount,
            newLabels: trace.newLabelCount,
          })}
        </span>
      </div>

      {trace.relPath !== null && (
        <p className="break-all text-xs text-muted" title={trace.relPath}>
          {trace.relPath}
        </p>
      )}

      {trace.error !== null && (
        <p className="inline-flex items-start gap-1.5 rounded-md border border-danger/40 bg-surface-2 p-2 text-xs text-danger">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="break-words">
            {t('aiTrace.errorLabel')} : {trace.error}
          </span>
        </p>
      )}

      <details>
        <summary className="cursor-pointer text-xs font-semibold text-fg">
          {t('aiTrace.promptLabel')}
        </summary>
        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-2 p-3 text-xs text-fg">
          {trace.prompt}
        </pre>
      </details>

      <details open>
        <summary className="cursor-pointer text-xs font-semibold text-fg">
          {t('aiTrace.responseLabel')}
        </summary>
        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-surface-2 p-3 text-xs text-fg">
          {trace.response ?? t('aiTrace.noResponse')}
        </pre>
      </details>
    </li>
  );
}

/** Pagination précédent/suivant. */
function Pagination({
  page,
  limit,
  total,
  loaded,
  onPrev,
  onNext,
}: {
  page: number;
  limit: number;
  total: number;
  loaded: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element | null {
  const { t } = useI18n();
  if (total <= limit) return null;
  const from = page * limit + 1;
  const to = page * limit + loaded;
  const hasPrev = page > 0;
  const hasNext = to < total;
  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        disabled={!hasPrev}
        onClick={onPrev}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('aiTrace.prevPage')}
      </button>
      <p className="text-sm text-muted" aria-live="polite">
        {interpolate(t('aiTrace.pageStatus'), { from, to, total })}
      </p>
      <button
        type="button"
        disabled={!hasNext}
        onClick={onNext}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('aiTrace.nextPage')}
      </button>
    </div>
  );
}
